/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { CheckboxDropdown, CustomStack, CustomToggleSwitch } from 'modules/common/components';
import ActionButton from 'modules/common/components/action-button';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import { CustomAggregation } from '../custom-aggregation';
/**
 * Breakdown window component common for the dashboard component
 * @param {Boolean} isDetailed - State of Detailed toggle
 * @param {*} chartBreakdown - Selected chart breakdown value
 * @param {Array} chartBreakdownList - chart breakdown list
 * @param {Function} handleIsDetailedChange -  Function to set isDetailed state change
 * @param {Function} handleBreakdownChange -  Function to set breakdown state change
 * @param {Boolean} isFocusOnAvailable - state of focus on section hide or show
 * @param {Array} focusOnList - focus on list
 * @param {*} focusOn - Selected focus on value
 * @param {*} focusOnLabel - focus on section label
 *  @param {Function} handleFocusOnChange -  Function to set focus on state change
 * @returns
 */
const BreakdownWindow = ({
  isDetailed,
  chartBreakdown,
  chartBreakdownList,
  handleIsDetailedChange,
  handleBreakdownChange,
  isFocusOnAvailable,
  focusOnList = [],
  focusOn = [],
  focusOnLabel = '',
  handleFocusOnChange = () => {},
  isGroupingAvailable = false,
  onGroupClick = () => {},
  setOpen = () => {},
  openGroupingDialog = false,
  showFilterName = false,
  setAName = '',
  setBName = '',
  selectedFilterName = '',
  isDeAggregateToggle = false,
  isTrendDeAggregate = false,
  isTrendDeAggregateDisabled = false,
  handleIsDeAggregateToggleChange = () => {},
  id = '',
  allSelected = false,
  isEnableBreakdownCheckbox = false,
  handlePrimaryChange = () => {},
  isPrimary,
}) => (
  <CustomStack cssClassName={styles.innerFilter}>
    <FormControl>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        {isEnableBreakdownCheckbox && (
          <CustomToggleSwitch
            label="Breakdown"
            title=""
            onChange={handlePrimaryChange}
            checked={isPrimary}
            className='breakdown-toggle'
          />
        )}
        {!isEnableBreakdownCheckbox && <Typography sx={{ color: '#0009' }}>Breakdown</Typography>}
        <FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={isDetailed}
                onChange={handleIsDetailedChange}
                id={`${id}-is-detailed`}
              />
            }
            sx={{ '& .css-1cn7wjw-MuiTypography-root': { fontSize: 12 } }}
            label="Details"
          />
        </FormControl>
      </Grid>
      {isDeAggregateToggle && (
        <CustomToggleSwitch
          label="VS. Time / Hotel"
          title=""
          onChange={handleIsDeAggregateToggleChange}
          checked={isTrendDeAggregate ?? false}
          disabled={isTrendDeAggregateDisabled}
          sx={{ pt: 4, pb: 4 }}
          className="time-hotel-breakdown-toggle"
        />
      )}
      <RadioGroup
        value={chartBreakdown ?? null}
        onChange={handleBreakdownChange}
        id={`${id}-breakdown`}
        className='segment-breakdown'
      >
        {chartBreakdownList?.map((item) => (
          <FormControlLabel
            key={item?.id}
            value={item?.id}
            control={<Radio />}
            label={item?.label}
            sx={{
              my: -1,
            }}
          />
        ))}
      </RadioGroup>
    </FormControl>
    {isFocusOnAvailable && (
      <FormControl className='focuson'>
        <CheckboxDropdown
          options={focusOnList}
          values={focusOn}
          label={focusOnLabel}
          onchange={handleFocusOnChange}
          isOptionEqualTo={(option, value) => option?.id === value?.id}
          id={id ? `${id}-focus-on` : ''}
          allSelected={allSelected}
        />
      </FormControl>
    )}
    {isGroupingAvailable && (
      <>
        <ActionButton
          id="auto-trend-custom-grouping"
          label="Custom Grouping"
          onClick={onGroupClick}
        />
        <CustomAggregation
          open={openGroupingDialog}
          onClose={() => setOpen(false)}
          focusOnList={focusOnList}
          breakdown={chartBreakdown}
        />
        {showFilterName && selectedFilterName && (
          <Typography id="auto-trend-filter-name">Custom Grouping: {selectedFilterName}</Typography>
        )}
        {showFilterName && setAName && <Typography>Group A: {setAName}</Typography>}
        {showFilterName && setBName && <Typography>Group B: {setBName}</Typography>}
      </>
    )}
  </CustomStack>
);
//
export default BreakdownWindow;
